/*
 * Delivery calculator page (/pages/delivery).
 *
 * Reads ?postcode=&suburb=&state= (set by the cart's Calculate button or by
 * this page's own picker), POSTs the destination to the JC Shipping worker's
 * /estimate endpoint, then asks whether the order includes fresh dog meals
 * or broths (two-stage reveal): Yes shows the chilled option card(s), No
 * the ambient one(s). An optional &chilled=true|false pre-answers the
 * question, and every selection writes it back to the URL so the current
 * view (suburb + answer) can be copied and shared as a link.
 *
 * The band also offers free store pickup as the alternative to delivery:
 * the pickup CTA (or ?pickup=true) renders a static pickup card in the
 * results panel — the store hours from the section's <template> and a
 * map of Chigwell (no API call).
 *
 * Rendering follows SHIPPING_ESTIMATOR.md §5:
 *   - §5.2 cost wording by pricing_basis (flat / per_box / tiered / live)
 *   - §5.3 delivery decision tree (delivery_note -> scheduled date -> window)
 *   - §5.6 cut-offs rendered from the ISO fields with concrete dates (the
 *     ready-made cutoff_label names weekdays without dates, so it is NOT
 *     used), plus a live "Order within ..." countdown line ticked
 *     client-side under the cut-off row
 *   - §5.4 chilled-unavailable rendered as an error-red card in the
 *     option-card layout, §5.5 not-serviced message
 *   - §5.8 ships_every_label is deliberately NOT rendered (no
 *     "Departs our kitchen" row on the cards)
 *
 * Responses are never cached (§5.9) — every Calculate re-POSTs.
 */
(function () {
  // Cloudflare Workers Custom Domain — per SHIPPING_ESTIMATOR.md §1 this is
  // the ONLY hostname theme code should call (workers.dev gets ad-blocked).
  var ENDPOINT = 'https://shipping.jackandchelo.com.au/estimate';

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // 1st / 2nd / 3rd / 4th ... 11th-13th are always 'th'.
  function ordinal(n) {
    var v = n % 100;
    if (v >= 11 && v <= 13) return n + 'th';
    switch (n % 10) {
      case 1: return n + 'st';
      case 2: return n + 'nd';
      case 3: return n + 'rd';
      default: return n + 'th';
    }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Pull the wall-clock parts straight out of the ISO string. order_cutoff
  // carries the Hobart offset and its wall-clock IS Hobart time, so parsing
  // the string (rather than converting through Date) renders the cut-off in
  // Hobart time for every visitor, per §5.6.
  function isoParts(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(iso || '');
    if (!m) return null;
    return { y: +m[1], mo: +m[2], d: +m[3], h: m[4] != null ? +m[4] : null, min: m[5] != null ? +m[5] : 0 };
  }

  function fmtDate(parts) {
    if (!parts) return '';
    // Local Date built from calendar parts — only used for the weekday name,
    // so no timezone shifting can occur.
    var dt = new Date(parts.y, parts.mo - 1, parts.d);
    return DAYS[dt.getDay()] + ', ' + ordinal(parts.d) + ' ' + MONTHS[parts.mo - 1];
  }

  function fmtTime(parts) {
    if (!parts || parts.h == null) return '';
    var h12 = parts.h % 12 === 0 ? 12 : parts.h % 12;
    var suffix = parts.h < 12 ? 'am' : 'pm';
    return h12 + (parts.min ? ':' + String(parts.min).padStart(2, '0') : '') + suffix;
  }

  // Which temperature class(es) the option covers — rendered as the card's
  // eyebrow above the carrier name.
  function appliesLabel(o) {
    var ap = o.applies_to || [];
    var chilled = ap.indexOf('chilled') !== -1;
    var ambient = ap.indexOf('ambient') !== -1;
    if (chilled && ambient) return 'Your order will be delivered by...';
    if (chilled) {
      // TAS-outside-local chilled options carry an `audience` (business -> SRT,
      // residential -> AusPost Express) so the two cards read differently.
      // Everywhere else there's no audience and the plain wording is kept.
      if (o.audience === 'business') return "If your order contains fresh meals or broths, and is being sent to a business, we'll send it using...";
      if (o.audience === 'residential') return "If your order contains fresh meals or broths, and is being sent to a residence, we'll send it using...";
      return "If your order contains fresh meals or broths, we'll send it using...";
    }
    if (ambient) return "If your order contains ambient items only, we'll send it using...";
    return '';
  }

  // §5.2 — unit-price wording; never present as an order total. The primary
  // price phrase (amount + its immediate qualifier) renders in the price
  // font; only the tiered overflow clause stays in body type.
  function priceHTML(o) {
    if (o.pricing_basis === 'live' || o.amount == null) {
      return '<span class="jc-estimator__price-amount">Calculated at checkout</span>';
    }
    var amt = esc(money(o.amount));
    if (o.pricing_basis === 'flat') {
      return '<span class="jc-estimator__price-amount">' + amt + ' per order</span>';
    }
    if (o.pricing_basis === 'per_box') {
      // AusPost Regular is priced live by ShipStation at checkout; its amount
      // is a per-box CAP, not a fixed rate. Every other per-box option
      // (AusPost Express today) is a flat per-box rate.
      if (o.id === 'auspost_regular') {
        return (
          '<span class="jc-estimator__price-amount">Cost calculated at checkout</span> ' +
          '<span class="jc-estimator__price-tiered">max price ' + amt + ' per box</span>'
        );
      }
      return '<span class="jc-estimator__price-amount">' + amt + ' per box</span>';
    }
    if (o.pricing_basis === 'tiered') {
      var extra = o.extra_per_box != null ? o.extra_per_box : o.amount;
      return '<span class="jc-estimator__price-amount">' + amt + ' (up to 2 boxes)</span> <span class="jc-estimator__price-tiered">then ' + esc(money(extra)) + ' per additional box</span>';
    }
    return '<span class="jc-estimator__price-amount">' + amt + '</span>';
  }

  // Card headline: the worker's carrier_name (per-zone local providers like
  // "Jack and Chelo Staff", "Be Cool", "Australia Post" / "Australia Post
  // Express"). Falls back to the option label (with the old AusPost Regular
  // shortening) if the field is ever absent.
  function displayLabel(o) {
    if (o.carrier_name) return o.carrier_name;
    if (o.id === 'auspost_regular') return 'Australia Post';
    return o.label;
  }

  // Carrier-specific delivery notes — the right-hand column of each card.
  // Theme-side copy for now; selected by carrierNotes() below.
  var NOTES = {
    jc_staff: [
      'By default, our driver will leave your box at your front door in a place they think is the most safe. If you would prefer it left somewhere else or have other specific delivery requirements like gate codes etc., please contact us.',
      'You will receive an SMS notification once your order has been delivered.',
      'All orders containing meals or broths will ship with an ice brick to keep your produce cool. Please pop your order into the fridge or freezer as soon as possible.',
      'If you have not received your order by 5pm, please let us know as soon as possible so we can investigate.',
      'Keen to give your box another life? We can re-use our shipping boxes and foil. Simply flatten and leave out when your next delivery is due and we will collect them for you.'
    ],
    local_courier: [
      'By default, the driver will leave your box at your front door in a place they think is the most safe. If you would prefer it left somewhere else or have other specific delivery requirements like gate codes etc., please contact us.',
      "Please keep an eye out for your delivery as you won't receive any other notification.",
      'All orders containing meals or broths will ship with an ice brick to keep your produce cool. Please pop your order into the fridge or freezer as soon as possible.',
      'If you have not received your order by 5pm, please let us know as soon as possible so we can investigate.'
    ],
    be_cool: [
      'By default, the driver will leave your box at your front door in a place they think is the most safe. If you would prefer it left somewhere else or have other specific delivery requirements like gate codes etc., please contact us.',
      'You will receive an SMS notification the night before your order is delivered, and again once your order has been delivered.',
      'All orders containing meals or broths will ship with an ice brick to keep your produce cool. Please pop your order into the fridge or freezer as soon as possible.',
      'If you do not receive your order on your expected day, please let us know as soon as possible so we can investigate.'
    ],
    srt: [
      'Orders shipped to a business address must have someone present to sign for the delivery. If your business is unattended at the time of delivery, you will need to schedule a redelivery.',
      'All orders containing meals or broths ship frozen. Please pop your order into a fridge or freezer as soon as it arrives.',
      'If you have not received your order on the expected day, please let us know as soon as possible so we can investigate.'
    ],
    auspost_express: [
      'By default, Australia Post will leave your box in a place they think is the most safe. If you would prefer it left somewhere else or have other specific delivery requirements like gate codes etc., please contact us.',
      'When your order is dispatched from our kitchen, you will receive an Australia Post tracking number via email and can use this to track your delivery.',
      'All orders will ship with an ice brick to keep your produce cool. Please pop your order into the fridge or freezer as soon as possible.',
      "If you are not able to find your order after it shows as 'Delivered' with the Australia Post tracking, please let us know as soon as possible so we can investigate."
    ],
    auspost_regular: [
      'By default, Australia Post will leave your box in a place they think is the most safe. If you would prefer it left somewhere else or have other specific delivery requirements like gate codes etc., please contact us.',
      'When your order is dispatched from our kitchen, you will receive an Australia Post tracking number via email and can use this to track your delivery.',
      "If you are not able to find your order after it shows as 'Delivered' with the Australia Post tracking, please let us know as soon as possible so we can investigate."
    ]
  };

  // Pick the notes list for an option. Local couriers split between our own
  // staff runs (carrier_id jc_* / carrier_name "Jack and Chelo ...") and
  // third-party locals (Kirk Ashlin, Littles, ...), which share one set.
  function carrierNotes(o) {
    var id = o.id || '';
    if (id === 'auspost_express') return NOTES.auspost_express;
    if (id === 'auspost_regular') return NOTES.auspost_regular;
    if (id === 'be_cool') return NOTES.be_cool;
    if (id === 'srt') return NOTES.srt;
    if (id === 'local_tas') {
      var cid = (o.carrier_id || '').toLowerCase();
      var cname = (o.carrier_name || '').toLowerCase();
      if (cid.indexOf('jc') === 0 || cname.indexOf('jack and chelo') !== -1) return NOTES.jc_staff;
      return NOTES.local_courier;
    }
    return null;
  }

  // §5.3 — exactly one branch applies per option, evaluated in this order.
  // Scheduled dates (and "Please contact us" zones) read "Next delivery
  // date:"; AusPost windows read "Next estimated delivery date:" with the
  // dates leading and the transit time in parentheses.
  function deliveryHTML(o, contactUrl) {
    if (o.delivery_note) {
      var note = esc(o.delivery_note);
      if (contactUrl) {
        note = '<a class="jc-estimator__contact" href="' + esc(contactUrl) + '">' + note + '</a>';
      }
      return 'Next delivery date: ' + note;
    }
    if (o.next_delivery_date) {
      return 'Next delivery date: <strong>' + esc(fmtDate(isoParts(o.next_delivery_date))) + '</strong>';
    }
    if (o.delivery_date_min) {
      var dmin = fmtDate(isoParts(o.delivery_date_min));
      var dmax = fmtDate(isoParts(o.delivery_date_max));
      var tmin = o.transit_business_days_min;
      var tmax = o.transit_business_days_max;
      if (o.delivery_date_min === o.delivery_date_max) {
        var days = tmin === 1 ? 'next business day' : tmin + ' business days';
        return 'Next estimated delivery date: <strong>' + esc(dmin) + '</strong> (' + esc(days) + ')';
      }
      return 'Next estimated delivery date: <strong>' + esc(dmin) + ' to ' + esc(dmax) + '</strong> (' + tmin + '–' + tmax + ' business days)';
    }
    if (o.ready_label) return esc(o.ready_label);
    return '';
  }

  // §5.6 — concrete dates only, built from the ISO fields.
  function cutoffHTML(o) {
    var parts = isoParts(o.order_cutoff);
    if (!parts) return '';
    return 'Next order cut-off: <strong>' + esc(fmtTime(parts)) + ' on ' + esc(fmtDate(parts)) + '</strong>';
  }

  // Live countdown to the cut-off, shown on its own line under the cut-off
  // row and re-ticked every second by init(). order_cutoff carries the
  // Hobart offset, so Date() resolves it to the right instant for every
  // visitor. Granularity steps down as the cut-off nears:
  //   >= 24h   "5 days and 12 hours" (no minutes; "and 0 hours" dropped)
  //   < 24h    "the next 6 hours" (ceiled, so the claim is never short)
  //   <= 1h    "the next 40 minutes" (ceiled)
  // Returns null once the cut-off has passed (the line hides — the next
  // Calculate brings the following cycle's cut-off).
  function countdownText(iso) {
    if (!iso) return null;
    var target = new Date(iso).getTime();
    if (!isFinite(target)) return null;
    var left = target - Date.now();
    if (left <= 0) return null;
    var mins = Math.ceil(left / 60000);
    var phrase;
    if (mins <= 60) {
      phrase = 'the next ' + mins + (mins === 1 ? ' minute' : ' minutes');
    } else if (mins < 1440) {
      var hrs = Math.ceil(mins / 60);
      phrase = 'the next ' + hrs + (hrs === 1 ? ' hour' : ' hours');
    } else {
      var d = Math.floor(mins / 1440);
      var h = Math.floor((mins % 1440) / 60);
      var bits = [d + (d === 1 ? ' day' : ' days')];
      if (h) bits.push(h + (h === 1 ? ' hour' : ' hours'));
      phrase = bits.join(' and ');
    }
    return 'Order within ' + phrase + ' to make the next shipment';
  }

  // The "Delivery Information for <suburb>, <STATE> <postcode>" heading.
  // Shared so the in-card top row and the standalone error/not-serviced
  // panels render identical markup.
  function destHeadingHTML(destText) {
    return '<h2 class="jc-estimator__dest">Delivery Information for ' + esc(destText) + '</h2>';
  }

  // coords: [lat, lng] when known, null when known-absent, undefined while
  // the postcode index is still loading (treated optimistically — the map
  // slot is reserved and filled on the re-render once coords resolve).
  function optionCardHTML(o, contactUrl, destText, coords) {
    var rows = [];
    var delivery = deliveryHTML(o, contactUrl);
    if (delivery) rows.push('<p class="jc-estimator__detail">' + delivery + '</p>');
    var cutoff = cutoffHTML(o);
    if (cutoff) rows.push('<p class="jc-estimator__detail">' + cutoff + '</p>');
    var countdown = countdownText(o.order_cutoff);
    if (countdown) {
      rows.push('<p class="jc-estimator__countdown" data-cutoff="' + esc(o.order_cutoff) + '">' + esc(countdown) + '</p>');
    }

    var applies = appliesLabel(o);

    // Carrier-specific delivery notes — the full-width row at the foot.
    var notes = carrierNotes(o);
    var notesHTML = '';
    if (notes && notes.length) {
      var items = '';
      for (var n = 0; n < notes.length; n++) {
        items += '<li>' + esc(notes[n]) + '</li>';
      }
      notesHTML =
        '<div class="jc-estimator__card-notes">' +
        '<h4 class="jc-estimator__notes-heading">Notes</h4>' +
        '<ul class="jc-estimator__notes">' + items + '</ul>' +
        '</div>';
    }

    var summaryHTML =
      '<div class="jc-estimator__card-main">' +
      (applies ? '<p class="jc-estimator__applies">' + esc(applies) + '</p>' : '') +
      '<h3 class="jc-estimator__option-label">' + esc(displayLabel(o)) + '</h3>' +
      '<p class="jc-estimator__price">' + priceHTML(o) + '</p>' +
      '</div>';
    var detailsHTML = rows.length ? '<div class="jc-estimator__card-details">' + rows.join('') + '</div>' : '';

    // With a map (the norm): the destination heading, summary and delivery
    // details stack in the left half, the suburb map fills the right half
    // from the top of the card, then Notes span the full width below.
    // Without coordinates (rare PO-box-only localities): a full-width
    // heading over summary | details, then Notes (no map).
    var hasMap = coords !== null;
    if (hasMap) {
      var mapAttrs = (coords && coords.length === 2)
        ? ' data-lat="' + coords[0] + '" data-lng="' + coords[1] + '"'
        : '';
      return (
        '<div class="jc-estimator__card jc-estimator__card--mapped">' +
        (destText ? destHeadingHTML(destText) : '') +
        '<div class="jc-estimator__card-left">' + summaryHTML + detailsHTML + '</div>' +
        '<div class="jc-estimator__map"' + mapAttrs + '></div>' +
        notesHTML +
        '</div>'
      );
    }
    return (
      '<div class="jc-estimator__card">' +
      (destText ? destHeadingHTML(destText) : '') +
      summaryHTML +
      detailsHTML +
      notesHTML +
      '</div>'
    );
  }

  function noticeHTML(message) {
    return '<div class="jc-estimator__notice">' + esc(message) + '</div>';
  }

  function init() {
    var root = document.getElementById('EstimatorApp');
    if (!root) return;

    var select = document.getElementById('EstimatorPostcode');
    var calcBtn = document.getElementById('EstimatorCalculate');
    var results = document.getElementById('EstimatorResults');
    var contactUrl = root.dataset.contactUrl || '';

    // view: 'delivery' (suburb + Calculate) or 'pickup' (the band's pickup
    // CTA); null until the visitor picks a path.
    var state = { view: null, dest: null, data: null, loading: false, error: null, choice: null, choice2: null };

    // What the previous render already showed — innerHTML re-renders rebuild
    // every node, so the slide-down class must be omitted from blocks that
    // were already on screen or they'd replay the animation on every click.
    var revealed = { stage: null, pickup: false };

    // --- suburb map (OpenFreeMap liberty via MapLibre) --------------------
    // jc-postcodes.json carries [SUBURB, STATE, POSTCODE, lat, lng] (built
    // by scripts/build_postcodes_coords.js), so the marker needs no
    // geocoding API. The index is loaded eagerly and lookups are synchronous
    // once ready; a card rendered before it loads re-renders when it lands.
    var mapInstance = null;
    var coordIndex = null; // 'postcode|SUBURB|STATE' -> [lat, lng]
    var coordByPc = null; // 'postcode' -> [lat, lng] (suburb-agnostic fallback)
    var coordPromise = null;

    function loadCoords() {
      if (coordPromise) return coordPromise;
      var url = (select && select.dataset.postcodesUrl) || '';
      if (!url) return Promise.resolve();
      coordPromise = fetch(url)
        .then(function (r) { return r.ok ? r.json() : []; })
        .then(function (rows) {
          coordIndex = new Map();
          coordByPc = new Map();
          for (var i = 0; i < rows.length; i++) {
            var row = rows[i];
            if (row.length < 5) continue;
            var lat = row[3];
            var lng = row[4];
            if (typeof lat !== 'number' || typeof lng !== 'number') continue;
            var pc = row[2];
            coordIndex.set(pc + '|' + String(row[0]).toUpperCase() + '|' + String(row[1]).toUpperCase(), [lat, lng]);
            if (!coordByPc.has(pc)) coordByPc.set(pc, [lat, lng]);
          }
          render(); // fill the map in if a card is already on screen
        })
        .catch(function () { /* map just won't show */ });
      return coordPromise;
    }

    // [lat, lng] when known, null when known-absent, undefined while loading.
    function lookupCoords(dest) {
      if (!coordIndex) return undefined;
      if (!dest) return null;
      var pc = dest.postcode || '';
      var key = pc + '|' + String(dest.suburb || '').toUpperCase() + '|' + String(dest.state || '').toUpperCase();
      return coordIndex.get(key) || coordByPc.get(pc) || null;
    }

    // Spin up the MapLibre map on the card's map slot (called after each
    // render; a no-op when no slot/coords are present).
    function renderMap() {
      if (typeof maplibregl === 'undefined') return;
      var el = results.querySelector('.jc-estimator__map[data-lat]');
      if (!el) return;
      var lat = parseFloat(el.getAttribute('data-lat'));
      var lng = parseFloat(el.getAttribute('data-lng'));
      if (!isFinite(lat) || !isFinite(lng)) return;
      mapInstance = new maplibregl.Map({
        container: el,
        style: 'https://tiles.openfreemap.org/styles/liberty',
        center: [lng, lat],
        zoom: 12,
        interactive: false,
        // Compact "i" toggle (the OSM credit stays reachable on click,
        // as OpenFreeMap requires).
        attributionControl: { compact: true }
      });
      new maplibregl.Marker({ color: '#004935' }).setLngLat([lng, lat]).addTo(mapInstance);

      // MapLibre re-expands the compact attribution when the style data
      // lands (async, after construction), so collapse it once the map
      // has loaded. Its own guard stops any later re-expansion.
      mapInstance.once('load', function () {
        var attrib = el.querySelector('.maplibregl-ctrl-attrib');
        if (attrib) {
          attrib.classList.remove('maplibregl-compact-show');
          attrib.removeAttribute('open');
        }
      });
    }

    function destLabel() {
      var d = state.dest || {};
      var bits = [];
      if (d.suburb) bits.push(d.suburb + ',');
      if (d.state) bits.push(d.state);
      if (d.postcode) bits.push(d.postcode);
      return bits.join(' ');
    }

    function footerHTML(skipDisclaimer) {
      if (skipDisclaimer) return '';
      return (
        '<p class="jc-estimator__disclaimer">' +
        'We do not ship to PO Boxes or Parcel Lockers.<br>' +
        'Local or national public holidays may affect the dispatch or arrival date for your order.<br>' +
        'The box count and shipping cost for your order will be automatically calculated at checkout.' +
        '</p>'
      );
    }

    function render() {
      // Any existing map is about to be wiped by the innerHTML re-render;
      // free its WebGL context first (browsers cap concurrent contexts).
      if (mapInstance) {
        try { mapInstance.remove(); } catch (e) { /* already gone */ }
        mapInstance = null;
      }

      // Pickup view takes over the whole results panel, independent of any
      // delivery destination/answer state (which is kept for a later
      // Calculate).
      if (state.view === 'pickup') {
        results.hidden = false;
        results.innerHTML = pickupHTML();
        revealed.pickup = true;
        renderMap();
        return;
      }

      if (!state.dest) {
        results.hidden = true;
        results.innerHTML = '';
        return;
      }
      results.hidden = false;

      // A failed estimate takes over the whole panel.
      if (state.error) {
        results.innerHTML =
          '<p class="jc-estimator__error">' + esc(state.error) + '</p>' +
          '<button type="button" class="jc-estimator__retry button" id="EstimatorRetry">Try again</button>';
        var retry = document.getElementById('EstimatorRetry');
        if (retry) retry.addEventListener('click', fetchEstimate);
        return;
      }

      var data = state.data;

      // §5.5 — destination not serviced at all (known only once data loads).
      // The heading renders standalone above the notice (there's no card).
      if (data && (!data.options || data.options.length === 0)) {
        results.innerHTML =
          destHeadingHTML(destLabel()) +
          noticeHTML(data.message || "Sorry, we don't currently deliver to this address.") +
          footerHTML();
        return;
      }

      // Single-stage reveal: show exactly ONE step at a time — the chilled
      // question, then (if relevant) the business-address question IN ITS PLACE,
      // then the results. Never stacked. Each step slides down once when it
      // first appears; a same-step re-render (e.g. the suburb map loading)
      // doesn't replay it. There is no back button — pressing Calculate again
      // resets to the first question to change an answer.
      var stage, html;
      if (!state.choice) {
        stage = 'chilled';
        html = questionHTML(stage !== revealed.stage);
      } else if (state.loading || !data) {
        stage = 'loading';
        html =
          '<div class="jc-estimator__answer' + (stage !== revealed.stage ? ' jc-estimator__step-in' : '') + '">' +
          '<p class="jc-estimator__loading">Calculating delivery options&hellip;</p>' +
          '</div>';
      } else if (state.choice === 'chilled' && data.business_question_applies && !state.choice2) {
        stage = 'business';
        html = businessQuestionHTML(stage !== revealed.stage);
      } else {
        stage = 'results';
        html =
          '<div class="jc-estimator__answer' + (stage !== revealed.stage ? ' jc-estimator__step-in' : '') + '">' +
          answerHTML(state.choice) +
          '</div>';
      }

      results.innerHTML = html;
      revealed.stage = stage;

      var btns = results.querySelectorAll('.jc-estimator__choice-btn[data-choice]');
      for (var b = 0; b < btns.length; b++) {
        btns[b].addEventListener('click', onChoice);
      }

      // Business-address sub-question buttons (TAS outside local, chilled only).
      var btns2 = results.querySelectorAll('.jc-estimator__choice-btn[data-choice2]');
      for (var c = 0; c < btns2.length; c++) {
        btns2[c].addEventListener('click', onChoice2);
      }

      // Chilled-unavailable card's CTA — same effect as answering No.
      var ambBtn = results.querySelector('.jc-estimator__ambient-btn');
      if (ambBtn) {
        ambBtn.addEventListener('click', function () {
          transitionStep(function () {
            state.choice = 'ambient';
            state.choice2 = null;
            syncUrl();
          });
        });
      }

      renderMap();
    }

    function choiceBtnHTML(choice, label) {
      var active = state.choice === choice;
      return (
        '<button type="button" class="jc-estimator__choice-btn' +
        (active ? ' is-active' : '') +
        '" data-choice="' + choice + '" aria-pressed="' + (active ? 'true' : 'false') + '">' +
        label +
        '</button>'
      );
    }

    function questionHTML(animate) {
      return (
        '<div class="jc-estimator__question-block' + (animate ? ' jc-estimator__step-in' : '') + '">' +
        '<p class="jc-estimator__question">Will your order include our fresh meals or broths?</p>' +
        '<p class="jc-estimator__question-sub">This determines whether we will send your order using refrigerated or ambient shipping</p>' +
        '<div class="jc-estimator__choice">' +
        choiceBtnHTML('chilled', 'Yes') +
        choiceBtnHTML('ambient', 'No') +
        '</div>' +
        '</div>'
      );
    }

    // Second-stage question for TAS destinations outside our local-delivery
    // area: a business address goes by SRT (our frozen courier, $20 flat), a
    // residential one by Australia Post Express. Shown only after "Yes" to the
    // chilled question and only when the worker flags business_question_applies.
    // It REPLACES the chilled question (single-stage reveal), so it carries its
    // own slide-down like the first question.
    function businessQuestionHTML(animate) {
      return (
        '<div class="jc-estimator__question-block' + (animate ? ' jc-estimator__step-in' : '') + '">' +
        '<p class="jc-estimator__question">Are you sending this order to a business address?</p>' +
        '<p class="jc-estimator__question-sub">Orders to a business in your area are sent with our dedicated frozen courier</p>' +
        '<div class="jc-estimator__choice">' +
        choice2BtnHTML('business', 'Yes') +
        choice2BtnHTML('residential', 'No') +
        '</div>' +
        '</div>'
      );
    }

    function choice2BtnHTML(choice2, label) {
      var active = state.choice2 === choice2;
      return (
        '<button type="button" class="jc-estimator__choice-btn' +
        (active ? ' is-active' : '') +
        '" data-choice2="' + choice2 + '" aria-pressed="' + (active ? 'true' : 'false') + '">' +
        label +
        '</button>'
      );
    }

    // Keep the URL in sync with the current view (destination + answer) so
    // the page state survives a copy-paste of the address bar.
    function syncUrl() {
      try {
        if (state.view === 'pickup') {
          history.replaceState(null, '', window.location.pathname + '?pickup=true');
          return;
        }
        if (!state.dest) return;
        var q =
          '?postcode=' + encodeURIComponent(state.dest.postcode) +
          '&suburb=' + encodeURIComponent(state.dest.suburb || '') +
          '&state=' + encodeURIComponent(state.dest.state || '');
        if (state.choice) q += '&chilled=' + (state.choice === 'chilled' ? 'true' : 'false');
        if (state.choice2) q += '&business=' + (state.choice2 === 'business' ? 'true' : 'false');
        history.replaceState(null, '', window.location.pathname + q);
      } catch (e) { /* non-fatal */ }
    }

    // Animate the current step out (slides up + fades), then apply the answer
    // and re-render so the next step rises up into its place — makes the change
    // obvious. Instant swap under prefers-reduced-motion.
    var transitioning = false;
    function transitionStep(applyChange) {
      if (transitioning) return;
      var current = results.firstElementChild;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!current || reduce) { applyChange(); render(); return; }
      transitioning = true;
      current.classList.remove('jc-estimator__step-in');
      current.classList.add('jc-estimator__step-out');
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        transitioning = false;
        applyChange();
        render();
      };
      current.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 220); // fallback if transitionend doesn't fire
    }

    function onChoice(e) {
      var choice = e.currentTarget.getAttribute('data-choice');
      transitionStep(function () {
        state.choice = choice;
        state.choice2 = null; // re-collapse the business sub-question when the chilled answer changes
        syncUrl();
      });
    }

    function onChoice2(e) {
      var choice2 = e.currentTarget.getAttribute('data-choice2');
      transitionStep(function () {
        state.choice2 = choice2;
        syncUrl();
      });
    }

    // The chosen temperature class picks the card(s): Yes -> chilled
    // option(s), No -> ambient. Local zones cover both, so either answer
    // lands on the same card there.
    function answerHTML(choice) {
      var data = state.data;

      // §5.4 — the order includes chilled items but chilled can't ship
      // here (e.g. NT/WA, or mainland outside the refrigerated-courier
      // zones). Theme-owned copy built from the destination, rendered as
      // a card styled identically to the option cards (heading + message
      // in the left half, suburb map right), plus a pink CTA that flips
      // the answer to ambient — revealing the ambient option already in
      // this response (no extra call).
      if (choice === 'chilled' && !data.chilled_available) {
        var d = state.dest || {};
        var place = ((d.suburb ? d.suburb + ' ' : '') + (d.postcode || '')).trim() || 'your area';
        var msg =
          "Sorry, we don't currently offer refrigerated delivery to " + place + '. ' +
          'We do offer ambient shipping for treats, snacks and accessories though. ' +
          "We're also continuing to open up new refrigerated delivery areas, so be sure to check back soon for updates.";
        var leftHTML =
          '<div class="jc-estimator__card-left">' +
          '<p class="jc-estimator__error-copy">' + esc(msg) + '</p>' +
          '<button type="button" class="jc-cta-orange jc-estimator__ambient-btn">Ambient delivery information</button>' +
          '</div>';
        var errDest = destLabel();
        var errCoords = lookupCoords(state.dest);
        var card;
        if (errCoords !== null) {
          var errMapAttrs = (errCoords && errCoords.length === 2)
            ? ' data-lat="' + errCoords[0] + '" data-lng="' + errCoords[1] + '"'
            : '';
          card =
            '<div class="jc-estimator__card jc-estimator__card--mapped jc-estimator__card--error">' +
            destHeadingHTML(errDest) +
            leftHTML +
            '<div class="jc-estimator__map"' + errMapAttrs + '></div>' +
            '</div>';
        } else {
          card =
            '<div class="jc-estimator__card jc-estimator__card--error">' +
            destHeadingHTML(errDest) +
            leftHTML +
            '</div>';
        }
        return card + footerHTML();
      }

      // The business-address question is a separate reveal step handled in
      // render(); by the time results render, choice2 is set — so narrow the
      // chilled matches to the chosen audience (SRT vs Express) below.
      var matches = (data.options || []).filter(function (o) {
        return (o.applies_to || []).indexOf(choice) !== -1;
      });
      // Once answered, narrow the chilled matches to the chosen audience so
      // only the SRT (business) or Express (residential) card shows.
      if (choice === 'chilled' && data.business_question_applies && state.choice2) {
        matches = matches.filter(function (o) { return o.audience === state.choice2; });
      }
      if (!matches.length) matches = (data.options || []).slice();

      // The destination heading renders inside each card as its top row;
      // the suburb map sits in the top-right (coords from the postcode index).
      var destText = destLabel();
      var coords = lookupCoords(state.dest);
      var cards = [];
      for (var i = 0; i < matches.length; i++) {
        cards.push(optionCardHTML(matches[i], contactUrl, destText, coords));
      }
      return '<div class="jc-estimator__options">' + cards.join('') + '</div>' + footerHTML();
    }

    // The pickup card — same layout as the delivery option cards: the
    // ready-within line and store hours stacked left, the Chigwell map
    // right. Fully static (no API call); the hours markup comes from the
    // section's <template id="EstimatorHours"> so the customiser stays
    // in control.
    function pickupHTML() {
      var tpl = document.getElementById('EstimatorHours');
      var hoursHTML = tpl ? tpl.innerHTML : '';

      var summaryHTML =
        '<div class="jc-estimator__card-main">' +
        '<p class="jc-estimator__applies">Pickup orders are usually ready within 24 hours.</p>' +
        '</div>';

      // Store address + phone — same detail-row style as the delivery
      // cards, below the italic line. Editable section settings (data-*
      // on the root); each row only shows when its value is set.
      var rows = [];
      if (root.dataset.storeAddress) {
        rows.push('<p class="jc-estimator__detail">Address: <strong>' + esc(root.dataset.storeAddress) + '</strong></p>');
      }
      if (root.dataset.storePhone) {
        rows.push('<p class="jc-estimator__detail">Phone: <strong>' + esc(root.dataset.storePhone) + '</strong></p>');
      }
      var detailsHTML = rows.length ? '<div class="jc-estimator__card-details">' + rows.join('') + '</div>' : '';

      var leftHTML = '<div class="jc-estimator__card-left">' + summaryHTML + detailsHTML + hoursHTML + '</div>';
      var headingHTML = '<h2 class="jc-estimator__dest">Pickup from our store</h2>';

      // Centre the pickup map on the actual store, not the Chigwell suburb
      // centroid that lookupCoords would return.
      var coords = [-42.8114079, 147.2392421];
      var card;
      if (coords !== null) {
        var mapAttrs = (coords && coords.length === 2)
          ? ' data-lat="' + coords[0] + '" data-lng="' + coords[1] + '"'
          : '';
        card =
          '<div class="jc-estimator__card jc-estimator__card--mapped">' +
          headingHTML + leftHTML +
          '<div class="jc-estimator__map"' + mapAttrs + '></div>' +
          '</div>';
      } else {
        card = '<div class="jc-estimator__card">' + headingHTML + leftHTML + '</div>';
      }

      var animate = revealed.pickup ? '' : ' jc-estimator__reveal';
      // Skip the delivery-specific disclaimer; keep the actions row.
      return '<div class="jc-estimator__options' + animate + '">' + card + '</div>' + footerHTML(true);
    }

    function fetchEstimate() {
      if (!state.dest) return;
      state.view = 'delivery';
      state.loading = true;
      state.error = null;
      state.data = null;
      state.choice = null; // re-ask the chilled question for each destination
      state.choice2 = null; // and re-collapse the business sub-question
      revealed.stage = null; // fresh destination -> the first question animates in
      render();

      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination: {
            suburb: state.dest.suburb || '',
            postcode: state.dest.postcode || '',
            state: state.dest.state || ''
          }
        })
      })
        .then(function (res) {
          return res.json().catch(function () { return null; }).then(function (body) {
            if (!res.ok) {
              throw new Error((body && body.error) || 'Could not calculate shipping right now. Please try again.');
            }
            return body;
          });
        })
        .then(function (data) {
          state.loading = false;
          state.data = data;
          render();
        })
        .catch(function (err) {
          state.loading = false;
          state.error = (err && err.message) || 'Could not calculate shipping right now. Please check your connection and try again.';
          render();
        });
    }

    // Keep the cut-off countdown line(s) live. Ticks every second but only
    // touches the DOM when the rendered text actually changes (once a
    // minute), so the cost is negligible.
    setInterval(function () {
      var els = results.querySelectorAll('.jc-estimator__countdown[data-cutoff]');
      for (var i = 0; i < els.length; i++) {
        var text = countdownText(els[i].getAttribute('data-cutoff'));
        if (text == null) {
          els[i].hidden = true;
        } else if (els[i].textContent !== text) {
          els[i].textContent = text;
        }
      }
    }, 1000);

    // The band's pickup CTA switches the results panel to the pickup view.
    var pickupBtn = document.getElementById('EstimatorPickup');
    if (pickupBtn) {
      pickupBtn.addEventListener('click', function () {
        state.view = 'pickup';
        syncUrl();
        render();
      });
    }

    // --- entry points -----------------------------------------------------

    // Warm the suburb-coordinate index up front so the map is ready by the
    // time a destination + answer are chosen.
    loadCoords();

    // 1. URL params (the cart's Calculate button redirects here with these;
    //    shared links may also carry a chilled=true|false answer).
    var params = new URLSearchParams(window.location.search);
    var pc = (params.get('postcode') || '').trim();
    var sb = (params.get('suburb') || '').trim();
    var st = (params.get('state') || '').trim().toUpperCase();
    var ch = (params.get('chilled') || '').trim().toLowerCase();
    var bz = (params.get('business') || '').trim().toLowerCase();
    if (/^\d{4}$/.test(pc) && st) {
      state.dest = { postcode: pc, suburb: sb, state: st };

      // Pre-fill the picker so the band reflects the destination being shown.
      if (select && select.tomselect) {
        var value = pc + '|' + sb.toUpperCase() + '|' + st;
        select.tomselect.addOption({
          value: value,
          label: (sb ? sb + ', ' : '') + st + ' ' + pc,
          suburb: sb,
          state: st,
          postcode: pc
        });
        select.tomselect.setValue(value); // onChange stashes the dataset
      }

      fetchEstimate();

      // Pre-answer the chilled question when the link carries it (set AFTER
      // fetchEstimate, which resets the choice for a fresh destination).
      if (ch === 'true') state.choice = 'chilled';
      else if (ch === 'false') state.choice = 'ambient';
      if (bz === 'true') state.choice2 = 'business';
      else if (bz === 'false') state.choice2 = 'residential';
    } else if ((params.get('pickup') || '').trim().toLowerCase() === 'true') {
      // Shared/bookmarked pickup view.
      state.view = 'pickup';
      render();
    }

    // 2. The page's own Calculate button (re-estimate without leaving).
    if (calcBtn) {
      calcBtn.addEventListener('click', function () {
        var d = (select && select.dataset) || {};
        if (!d.postcode) {
          if (select && select.tomselect) select.tomselect.open();
          return;
        }
        state.dest = { postcode: d.postcode, suburb: d.suburb || '', state: d.state || '' };
        fetchEstimate();
        syncUrl(); // after fetchEstimate clears the choice, dropping any stale chilled param
      });
    }

    // 3. Arrived from the cart's #DeliveryBand link — scroll the band into
    //    view beneath the sticky header. scrollIntoView honours the band's
    //    scroll-margin-top; running it here (after sticky-header.js has set
    //    --header-height) gets the offset right even if the browser's
    //    initial fragment scroll fired before that variable existed.
    if (window.location.hash === '#DeliveryBand') {
      var band = document.getElementById('DeliveryBand');
      if (band) requestAnimationFrame(function () { band.scrollIntoView(); });
    }
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
